import React, { useEffect, useState, useRef } from "react";
import "./Style/Game.css";
import back_n from "./Image/back_n.gif";
import done_o from "./Image/done_o.gif";
import done_d from "./Image/done_d.gif";
import done_n from "./Image/done_n.gif";
import { useNavigate, useLocation } from "react-router-dom";
import Loading from "../../Common/Loading";

const Game: React.FC = () => {
  const [gameContent, setGameContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [hasSaved, setHasSaved] = useState(false);
  const [navLoading, setNavLoading] = useState(false);
  const [doneSrc, setDoneSrc] = useState(done_n);
  const startTimeRef = useRef<number>(Date.now());
  const hasSavedRef = useRef<boolean>(false);
  const savePromiseRef = useRef<Promise<any> | null>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);

  const rawLsnCode = params.get("LsnTestCode");
  const cleanLessonCode = rawLsnCode?.replace(/_E$/, "") || "";
  const dicCode = params.get("DICCode") || cleanLessonCode;
  const isMemoryGame = cleanLessonCode.startsWith("GAME_MATCH");
  const calledFrom = params.get("calledFrom");
  const subject = sessionStorage.getItem("Subject");

  const rawHwid = params.get("HWID") || params.get("hwid");
  let hwid: number | null = null;
  if (rawHwid !== null && rawHwid !== undefined && rawHwid !== "null" && rawHwid !== "") {
      hwid = parseInt(rawHwid);
  }

  const SchoolCode = sessionStorage.getItem("SchoolCode");
  const FamilyCode = sessionStorage.getItem("FamilyCode");
  const Login = sessionStorage.getItem("Login");
  
  const apiUrl = process.env.REACT_APP_DOCUMENT_LINK ;

  const clearCompletionCaches = async () => {
    if (!SchoolCode || !FamilyCode || !Login) return;

    try {
      await fetch(`${apiUrl}clear_cache/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          SchoolCode,
          FamilyCode,
          Login,
          Caches: ["all_user"],
        }),
      });
    } catch (error) {
      // Saving the completed game must not be hidden by a cache failure.
      console.error("Error clearing completion caches:", error);
    }
  };

  useEffect(() => {
    const initGame = async () => {
      try {
        setLoading(true);

        const contentRes = await fetch(`${apiUrl}get_game_content/`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            school_code: SchoolCode,
            family_code: FamilyCode,
            login: Login,
            lesson_code: cleanLessonCode,
          }),
        });

        if (!contentRes.ok) throw new Error("Failed to load game content");
        
        const contentData = await contentRes.json();
        setGameContent(contentData.gameContent || "");

        await fetch(`${apiUrl}track_game_start/`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            school_code: SchoolCode,
            family_code: FamilyCode,
            login: Login,
            dic_code: dicCode,
            lesson_code: cleanLessonCode,
            hwid: hwid,
            subject: subject
          }),
        });
        
        // console.log("Game Session Started (Status N)");

      } catch (err) {
        console.error("Error initializing game:", err);
      } finally {
        startTimeRef.current = Date.now();
        setLoading(false);
      }
    };

    if (SchoolCode && Login) {
      initGame();
    }
  }, []);

  useEffect(() => {
  const handleGameMessage = (event: MessageEvent) => {
    if (event.data && event.data.type === "GAME_COMPLETE" && !hasSavedRef.current) {
      const { score, time } = event.data;

      let finalScore = score;

      if (isMemoryGame && (!finalScore || finalScore <= 0)) {
        finalScore = 100;
      }

      setHasSaved(true);
      setNavLoading(true);

      let finalTime: number;

      const wallClockTime = Math.floor(
        (Date.now() - startTimeRef.current) / 1000
      );

      let parsedTime = parseFloat(time);

      if (typeof time === 'string' && time.includes(':')) {
        try {
          const cleanTime = time.replace(/[^\d:]/g, ''); 
          const parts = cleanTime.split(':').map(Number);
          
          if (parts.length === 3) {
            parsedTime = parts[0] * 3600 + parts[1] * 60 + parts[2];
          } else if (parts.length === 2) {
            parsedTime = parts[0] * 60 + parts[1];
          } else if (parts.length === 1) {
            parsedTime = parts[0];
          }
        } catch (e) {
          console.error("Time parse error", e);
          parsedTime = NaN;
        }
      }

      if (!isNaN(parsedTime) && parsedTime > 0) {
        finalTime = Math.round(parsedTime);
      } else {
        finalTime = wallClockTime;
      }

      hasSavedRef.current = true;

      const savePromise = fetch(`${apiUrl}save_game_session/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          schoolCode: SchoolCode,
          familyCode: FamilyCode,
          login: Login,
          dicCode: dicCode,
          lessonCode: cleanLessonCode,
          bestScore: finalScore,
          bestTime: finalTime,
          hwid: hwid,
          subject: subject
        }),
      })
        .then((res) => res.json())
        .catch((err) => {
          console.error("Save Error:", err);
          throw err;
        })
        .finally(() => {
          savePromiseRef.current = null;
          setNavLoading(false);
        });

      savePromiseRef.current = savePromise;
    }
  };

  window.addEventListener("message", handleGameMessage);
  return () => window.removeEventListener("message", handleGameMessage);
}, [SchoolCode, FamilyCode, Login, dicCode, cleanLessonCode, apiUrl, hwid]);


  const forceSaveGame = async () => {
    // 1. Prevent double-save
    if (hasSavedRef.current) {
      return;
    }
  
    setHasSaved(true);
    hasSavedRef.current = true;
    setNavLoading(true);
  
    let finalTime = Math.floor(
      (Date.now() - startTimeRef.current) / 1000
    );
    let finalScore = isMemoryGame ? 100 : 0;

    try {
        const storedScore = sessionStorage.getItem("finalScore");
        const storedTime = sessionStorage.getItem("finalTime");

        if (storedScore) {
            const parsedScore = parseFloat(storedScore);
            if (!isNaN(parsedScore)) finalScore = parsedScore;
        }

        if (storedTime) {
            let parsed = parseFloat(storedTime);

            if (typeof storedTime === 'string' && storedTime.includes(':')) {
                const parts = storedTime.replace(/[^\d:]/g, '').split(':').map(Number);
                if (parts.length === 3) parsed = parts[0] * 3600 + parts[1] * 60 + parts[2];
                else if (parts.length === 2) parsed = parts[0] * 60 + parts[1];
                else if (parts.length === 1) parsed = parts[0];
            }

            if (!isNaN(parsed) && parsed > 0) {
                finalTime = parsed; 
            }
        }
    } catch (e) {
        console.error("Error reading storage in forceSave:", e);
    }
  
    try {
      await fetch(`${apiUrl}save_game_session/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          schoolCode: SchoolCode,
          familyCode: FamilyCode,
          login: Login,
          dicCode: dicCode,
          lessonCode: cleanLessonCode,
          bestScore: finalScore,
          bestTime: finalTime,
          hwid: hwid,
          subject: subject
        }),
      });
    } catch (err) {
      console.error("Force save failed:", err);
    } finally {
      setNavLoading(false);
    }
  };

  const goBack = async () => {
    setNavLoading(true);
    if (!hasSavedRef.current) {
      await forceSaveGame();
    }

    setTimeout(() => {
      if (calledFrom) navigate(decodeURIComponent(calledFrom));
      else navigate("/STUDENT/MSR/ExploreActivities");
    }, 200);
  };

  const finishGame = async () => {
    setNavLoading(true);
    if (!hasSavedRef.current) {
      await forceSaveGame();
    } else if (savePromiseRef.current) {
      try {
        await savePromiseRef.current;
      } catch {
        // savePromise already records the failure; continue so the user is
        // not trapped on the completed game.
      }
    }

    await clearCompletionCaches();

    setTimeout(() => {
      if (calledFrom) navigate(decodeURIComponent(calledFrom));
      else navigate("/STUDENT/MSR/ExploreActivities");
    }, 200);
  };

  const handleDoneMouseDown = () => setDoneSrc(done_d);
  const handleDoneMouseUp = () => setDoneSrc(done_o);
  const handleDoneMouseOut = () => setDoneSrc(done_n);
  const handleDoneMouseOver = () => setDoneSrc(done_o);

  return (
    <form id="form1">
      {(loading || navLoading) && <Loading />}

      <div className="tblMain">
        <div className="trNavBar">
          <div className="tdNavBarT">
            <div id="imgBtnBack" className="imgBtnBackT" onClick={goBack}>
              <img src={back_n} alt="Back" width="80" height="30" />
            </div>
 
            <div
              id="imgbtnGameCompleted"
              className="imgbtnGameCompletedT"
              onClick={finishGame}
            >
              <img
                src={doneSrc}
                alt="Done"
                width="80"
                height="30"
                onMouseOut={handleDoneMouseOut}
                onMouseDown={handleDoneMouseDown}
                onMouseUp={handleDoneMouseUp}
                onMouseOver={handleDoneMouseOver}
              />
            </div>
          </div>
        </div>

        <div className="trtitle">
          <label id="lblTitle"></label>
        </div>

        <div id="container">
          <div id="divGameHolder" className="divGameHolderT">
            {!loading && (
              <iframe
                className="sizer"
                style={{ height: "100%", width: "100%" }}
                srcDoc={gameContent}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            )}

          </div>
        </div>
      </div>
    </form>
  );
};

export default Game;
